// Listado de TODOS los usuarios registrados (profiles).
// Muestra: contacto + role + cuántas suscripciones activas + cuántos pedidos one-shot.
// Filtros: todos | sin compras | suscriptores | compradores one-shot.
// Solo superadmin puede INVITAR usuarios nuevos y cambiar roles (2026-06-10).

import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { useAuth } from '../../lib/AuthContext'
import { supabase } from '../../lib/supabase'

const ROLE_LABEL = {
  superadmin: '👑 Superadmin',
  admin: '🛡️ Admin',
  marketing: '📣 Marketing',
  repartidor: '🚐 Repartidor',
  portero: '🛎️ Portero',
  cliente: '👤 Cliente',
}

const ROLE_COLOR = {
  superadmin: { bg: '#ede9fe', color: '#5b21b6' },
  admin: { bg: '#fee2e2', color: '#991b1b' },
  marketing: { bg: '#fce7f3', color: '#9d174d' },
  repartidor: { bg: '#dbeafe', color: '#1e40af' },
  portero: { bg: '#fef3c7', color: '#92400e' },
  cliente: { bg: '#f1f5f9', color: '#475569' },
}

// Roles que se pueden asignar desde el modal de invitar (excluye 'cliente' que se crea por la tienda)
const ROLES_INVITABLES = [
  { value: 'admin',      label: '🛡️ Admin',       desc: 'Ve todo el panel (lectura)' },
  { value: 'marketing',  label: '📣 Marketing',   desc: 'Solo sección Web & Marketing' },
  { value: 'repartidor', label: '🚐 Repartidor',  desc: 'App de entregas' },
  { value: 'superadmin', label: '👑 Superadmin',  desc: 'Puede modificar TODO + crear usuarios' },
]

const fmtFecha = (d) => d ? new Date(d).toLocaleDateString('es-UY', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'

export default function Usuarios() {
  const { isSuperadmin } = useAuth()
  const [usuarios, setUsuarios] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  // Filtros vienen de la URL para que cada segmento sea linkeable (campañas).
  // Ej: /admin/usuarios?tipo=b2c&filtro=suscriptores&tag=vip
  const [searchParams, setSearchParams] = useSearchParams()
  const filtro = searchParams.get('filtro') || 'todos'  // todos | sin_compras | suscriptores | one_shot
  const tipo   = searchParams.get('tipo')   || 'todos'  // todos | b2c | b2b | porteros | repartidores | internos
  const tag    = searchParams.get('tag')    || ''       // filtrar por una tag específica
  const setFiltro = (v) => { const sp = new URLSearchParams(searchParams); v === 'todos' ? sp.delete('filtro') : sp.set('filtro', v); setSearchParams(sp, { replace: true }) }
  const setTipo   = (v) => { const sp = new URLSearchParams(searchParams); v === 'todos' ? sp.delete('tipo')   : sp.set('tipo', v);   setSearchParams(sp, { replace: true }) }
  const setTag    = (v) => { const sp = new URLSearchParams(searchParams); !v          ? sp.delete('tag')    : sp.set('tag', v);    setSearchParams(sp, { replace: true }) }
  const [busqueda, setBusqueda] = useState('')
  const [modalInvitar, setModalInvitar] = useState(false)
  const [modalInvitarCliente, setModalInvitarCliente] = useState(false)
  // editando: deprecado 2026-06-15. La edición pasó a /admin/usuarios/:id (UsuarioDetalle.jsx)
  const [msg, setMsg] = useState(null)

  useEffect(() => { cargar() }, [])

  async function cargar() {
    setLoading(true)
    setError(null)
    try {
      // 1) Profiles
      const { data: profiles, error: pe } = await supabase
        .from('profiles')
        .select('id, nombre, email, phone, documento, documento_tipo, role, created_at, transferencia_habilitada, tags')
        .order('created_at', { ascending: false })
        .limit(2000)  // paliativo: cuando lleguemos a 1500+ migrar a RPC server-side (task #19)
      if (pe) throw pe

      // 2) Suscripciones (clientes) — agrupadas por titular_id y beneficiario_id
      const { data: subs, error: se } = await supabase
        .from('clientes')
        .select('id, estado, titular_id, beneficiario_id, empresa_id, fuera_zona')
      if (se) throw se

      // 3) Pedidos one-shot — pedidos que no son de suscripción
      //    Algunos pedidos pueden no tener cliente_id (visitor). Los ignoramos para conteo por usuario.
      const { data: peds, error: pee } = await supabase
        .from('pedidos')
        .select('id, estado, tipo, comprador_profile_id, monto_total')
        .in('tipo', ['compra_unica', 'productos_sueltos'])
      if (pee) throw pee

      // 4) Mapas de conteo
      const subsActivasPorTitular = new Map()
      const subsPorPersona = new Map()  // cuenta cualquier rol (titular o beneficiario)
      const b2bPorPersona = new Set()           // true si tiene alguna suscripción B2B (empresa_id != null)
      const fueraZonaPorPersona = new Set()     // true si tiene alguna suscripción con fuera_zona=true
      ;(subs || []).forEach(s => {
        if (s.titular_id) {
          subsPorPersona.set(s.titular_id, (subsPorPersona.get(s.titular_id) || 0) + 1)
          if (s.estado === 'activa') {
            subsActivasPorTitular.set(s.titular_id, (subsActivasPorTitular.get(s.titular_id) || 0) + 1)
          }
          if (s.empresa_id) b2bPorPersona.add(s.titular_id)
          if (s.fuera_zona) fueraZonaPorPersona.add(s.titular_id)
        }
        if (s.beneficiario_id) {
          subsPorPersona.set(s.beneficiario_id, (subsPorPersona.get(s.beneficiario_id) || 0) + 1)
          if (s.empresa_id) b2bPorPersona.add(s.beneficiario_id)
          if (s.fuera_zona) fueraZonaPorPersona.add(s.beneficiario_id)
        }
      })

      const pedidosPorComprador = new Map()
      const pedidosPagadosPorComprador = new Map()
      const montoPorComprador = new Map()
      ;(peds || []).forEach(p => {
        if (!p.comprador_profile_id) return
        pedidosPorComprador.set(p.comprador_profile_id, (pedidosPorComprador.get(p.comprador_profile_id) || 0) + 1)
        if (p.estado === 'pagado' || p.estado === 'entregado' || p.estado === 'en_armado' || p.estado === 'en_ruta') {
          pedidosPagadosPorComprador.set(p.comprador_profile_id, (pedidosPagadosPorComprador.get(p.comprador_profile_id) || 0) + 1)
          montoPorComprador.set(p.comprador_profile_id, (montoPorComprador.get(p.comprador_profile_id) || 0) + Number(p.monto_total || 0))
        }
      })

      // 5) Construir filas
      const filas = (profiles || []).map(p => ({
        ...p,
        subs_total: subsPorPersona.get(p.id) || 0,
        subs_activas: subsActivasPorTitular.get(p.id) || 0,
        pedidos_total: pedidosPorComprador.get(p.id) || 0,
        pedidos_pagados: pedidosPagadosPorComprador.get(p.id) || 0,
        monto_one_shot: montoPorComprador.get(p.id) || 0,
        es_b2b: b2bPorPersona.has(p.id),
        es_fuera_zona: fueraZonaPorPersona.has(p.id),
      }))

      setUsuarios(filas)
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  const filtrados = useMemo(() => {
    let r = usuarios
    // Filtro por tipo de usuario
    if (tipo === 'b2c') r = r.filter(u => u.role === 'cliente' && !u.es_fuera_zona)
    else if (tipo === 'fuera_zona') r = r.filter(u => u.es_fuera_zona)
    else if (tipo === 'porteros') r = r.filter(u => u.role === 'portero')
    else if (tipo === 'repartidores') r = r.filter(u => u.role === 'repartidor')
    else if (tipo === 'internos') r = r.filter(u => ['admin','superadmin','marketing'].includes(u.role))
    // Filtro por actividad
    if (filtro === 'sin_compras') {
      r = r.filter(u => u.subs_total === 0 && u.pedidos_pagados === 0)
    } else if (filtro === 'suscriptores') {
      r = r.filter(u => u.subs_activas > 0)
    } else if (filtro === 'one_shot') {
      r = r.filter(u => u.pedidos_pagados > 0 && u.subs_activas === 0)
    }
    // Filtro por tag (segmento)
    if (tag) {
      r = r.filter(u => Array.isArray(u.tags) && u.tags.includes(tag))
    }
    if (busqueda.trim()) {
      const q = busqueda.toLowerCase()
      r = r.filter(u =>
        u.nombre?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.phone?.toLowerCase().includes(q) ||
        (u.documento || '').toLowerCase().includes(q)
      )
    }
    return r
  }, [usuarios, filtro, tipo, tag, busqueda])

  // Todas las tags únicas en uso (para autocompletar el filtro)
  const tagsDisponibles = useMemo(() => {
    const s = new Set()
    usuarios.forEach(u => (u.tags || []).forEach(t => s.add(t)))
    return Array.from(s).sort()
  }, [usuarios])

  // Export CSV — respeta filtros activos
  function exportarCSV() {
    const headers = ['nombre', 'email', 'telefono', 'documento', 'rol', 'tipo', 'suscripciones_activas', 'pedidos_pagados', 'monto_oneshot', 'tags', 'alta']
    const rows = filtrados.map(u => [
      (u.nombre || '').replace(/[,\n"]/g, ' '),
      u.email || '',
      u.phone || '',
      u.documento ? `${(u.documento_tipo || '').toUpperCase()} ${u.documento}` : '',
      u.role || '',
      u.es_fuera_zona ? 'Fuera-zona' : (u.role === 'cliente' ? 'B2C' : u.role || ''),
      u.subs_activas || 0,
      u.pedidos_pagados || 0,
      u.monto_one_shot || 0,
      (u.tags || []).join(';'),
      u.created_at ? new Date(u.created_at).toISOString().slice(0, 10) : '',
    ])
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' })  // BOM para Excel
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    const stamp = new Date().toISOString().slice(0, 10)
    const seg = [tipo !== 'todos' && tipo, filtro !== 'todos' && filtro, tag && `tag-${tag}`].filter(Boolean).join('_') || 'todos'
    link.href = url
    link.download = `limpack-usuarios-${seg}-${stamp}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const counts = useMemo(() => ({
    todos: usuarios.length,
    sin_compras: usuarios.filter(u => u.subs_total === 0 && u.pedidos_pagados === 0).length,
    suscriptores: usuarios.filter(u => u.subs_activas > 0).length,
    one_shot: usuarios.filter(u => u.pedidos_pagados > 0 && u.subs_activas === 0).length,
    b2c: usuarios.filter(u => u.role === 'cliente' && !u.es_fuera_zona).length,
    fuera_zona: usuarios.filter(u => u.es_fuera_zona).length,
    porteros: usuarios.filter(u => u.role === 'portero').length,
    repartidores: usuarios.filter(u => u.role === 'repartidor').length,
    internos: usuarios.filter(u => ['admin','superadmin','marketing'].includes(u.role)).length,
  }), [usuarios])

  return (
    <AdminLayout>
      <div className="page">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap', marginBottom: 4 }}>
          <div>
            <h1>👤 Usuarios</h1>
            <p className="muted">Todas las personas registradas en la plataforma. Suscriptores, compradores one-shot y leads sin compra.</p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={exportarCSV}
              disabled={filtrados.length === 0}
              title={`Exportar ${filtrados.length} usuarios filtrados a CSV`}
              style={{ padding: '8px 14px', background: '#fff', border: '1px solid #cbd5e1', color: '#475569', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: filtrados.length ? 'pointer' : 'not-allowed', fontFamily: 'inherit', opacity: filtrados.length ? 1 : 0.5 }}
            >⬇ Exportar CSV ({filtrados.length})</button>
            {isSuperadmin && (
              <button className="usu-btn-invitar" onClick={() => setModalInvitar(true)}>
                ＋ Invitar usuario
              </button>
            )}
            {isSuperadmin && (
              <button className="usu-btn-invitar" onClick={() => setModalInvitarCliente(true)} style={{ background: '#00A8B5' }} title="Crear cuenta de cliente y mandarle el mail de invitación">
                ＋ Invitar cliente
              </button>
            )}
          </div>
        </div>

        {msg && (
          <div className={`usu-msg usu-msg-${msg.tipo}`}>
            {msg.txt}
          </div>
        )}

        {/* Stat cards */}
        <div className="cards-grid" style={{ marginBottom: 16 }}>
          <div className="card">
            <h3>Total registrados</h3>
            <div className="stat">{counts.todos}</div>
          </div>
          <div className="card">
            <h3>Suscriptores activos</h3>
            <div className="stat" style={{ color: '#10B981' }}>{counts.suscriptores}</div>
          </div>
          <div className="card">
            <h3>One-shot</h3>
            <div className="stat" style={{ color: '#3b82f6' }}>{counts.one_shot}</div>
          </div>
          <div className="card">
            <h3>Sin compras</h3>
            <div className="stat" style={{ color: '#f59e0b' }}>{counts.sin_compras}</div>
          </div>
        </div>

        {/* Filtro por TIPO de usuario */}
        <div className="ap-filtros" style={{ marginBottom: 6 }}>
          {[
            { v: 'todos',        label: 'Todos',           c: counts.todos },
            { v: 'b2c',          label: '👤 B2C',           c: counts.b2c },
            { v: 'fuera_zona',   label: '🚫 Fuera zona',    c: counts.fuera_zona },
            { v: 'porteros',     label: '🛎️ Porteros',      c: counts.porteros },
            { v: 'repartidores', label: '🚐 Repartidores',  c: counts.repartidores },
            { v: 'internos',     label: '🛡 Internos',      c: counts.internos },
          ].map(o => (
            <button key={o.v}
              className={`ap-filtro ${tipo === o.v ? 'is-active' : ''}`}
              onClick={() => setTipo(o.v)}>
              {o.label} <em>{o.c}</em>
            </button>
          ))}
          {/* B2B: las empresas son entidades distintas, no usuarios. Viven en /admin/empresas */}
          <Link to="/admin/empresas" className="ap-filtro" style={{ marginLeft: 'auto', color: '#854d0e', borderColor: '#fef3c7', background: '#fffbeb' }} title="Las empresas B2B se gestionan aparte">
            🏢 Empresas (B2B) →
          </Link>
        </div>

        {/* Filtro por ACTIVIDAD */}
        <div className="ap-filtros" style={{ marginBottom: 12, opacity: 0.85 }}>
          {[
            { v: 'todos',        label: 'Toda actividad',          c: counts.todos },
            { v: 'suscriptores', label: '🎁 Suscriptores',          c: counts.suscriptores },
            { v: 'one_shot',     label: '🛍️ One-shot',             c: counts.one_shot },
            { v: 'sin_compras',  label: '🌱 Sin compras (leads)',   c: counts.sin_compras },
          ].map(o => (
            <button key={o.v}
              className={`ap-filtro ${filtro === o.v ? 'is-active' : ''}`}
              onClick={() => setFiltro(o.v)}>
              {o.label} <em>{o.c}</em>
            </button>
          ))}
        </div>

        {/* Filtro por TAG (segmento) */}
        {tagsDisponibles.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em' }}>🏷 Tag:</span>
            <select
              value={tag}
              onChange={e => setTag(e.target.value)}
              style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, color: '#475569', fontFamily: 'inherit', background: tag ? '#cffafe' : '#fff' }}
            >
              <option value="">— ningún tag —</option>
              {tagsDisponibles.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            {tag && (
              <button onClick={() => setTag('')} style={{ padding: '4px 10px', background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>× quitar</button>
            )}
            <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 'auto' }}>{filtrados.length} {filtrados.length === 1 ? 'usuario' : 'usuarios'} en este segmento</span>
          </div>
        )}

        {/* Buscador */}
        <input
          type="search"
          placeholder="Buscar por nombre, email, teléfono o documento…"
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
          style={{
            width: '100%', padding: '10px 14px', marginBottom: 16,
            border: '1px solid #d1d5db', borderRadius: 10, fontSize: 14,
          }}
        />

        {error && <div style={{ padding: 12, background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, color: '#991b1b', marginBottom: 12 }}>{error}</div>}
        {loading && <div className="ap-loading">Cargando…</div>}
        {!loading && usuarios.length >= 2000 && (
          <div style={{ padding: 10, background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8, color: '#92400e', fontSize: 12, marginBottom: 12 }}>
            ⚠ Llegaste al tope de 2000 usuarios cargados. Refiná los filtros para ver más específicos. (Próximamente: paginación server-side — task #19)
          </div>
        )}

        {!loading && filtrados.length === 0 && (
          <div className="ap-empty">
            <div style={{ fontSize: 48 }}>🔍</div>
            <h3>Sin resultados</h3>
            <p>Probá ajustar los filtros o limpiar la búsqueda.</p>
          </div>
        )}

        {!loading && filtrados.length > 0 && (
          <div style={{ overflowX: 'auto', background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' }}>
            <table className="usu-table">
              <thead>
                <tr>
                  <th>Usuario</th>
                  <th>Contacto</th>
                  <th>Doc.</th>
                  <th>Rol</th>
                  <th style={{ textAlign: 'center' }}>Suscrip.</th>
                  <th style={{ textAlign: 'center' }}>One-shot</th>
                  <th>Alta</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtrados.map(u => {
                  const rc = ROLE_COLOR[u.role] || ROLE_COLOR.cliente
                  return (
                    <tr key={u.id} style={{ height: 46 }}>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <div style={{ fontWeight: 700, color: '#1a3461', display: 'flex', alignItems: 'center', gap: 6 }}>
                          {u.nombre || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Sin nombre</span>}
                          {u.es_fuera_zona && <span title="Cliente fuera de zona de cobertura" style={{ fontSize: 10, fontWeight: 700, color: '#9A3412', background: '#FED7AA', padding: '1px 6px', borderRadius: 4 }}>FZ</span>}
                          {u.transferencia_habilitada && (
                            <span title="Habilitado para transferencia" style={{ fontSize: 11, color: '#0e7490' }}>🏦</span>
                          )}
                        </div>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {u.email && <div style={{ fontSize: 12, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}>📧 {u.email}</div>}
                        {u.phone && <div style={{ fontSize: 11, color: '#6b7280' }}>📱 {u.phone}</div>}
                      </td>
                      <td style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>
                        {u.documento ? `${(u.documento_tipo || '').toUpperCase()} ${u.documento}` : '—'}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <span className="usu-pill" style={{ background: rc.bg, color: rc.color }}>
                          {ROLE_LABEL[u.role] || u.role}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                        {u.subs_activas > 0
                          ? <span style={{ display: 'inline-block', minWidth: 22, padding: '2px 8px', background: '#d1fae5', color: '#065f46', borderRadius: 999, fontSize: 12, fontWeight: 700 }} title={u.subs_total > u.subs_activas ? `${u.subs_total - u.subs_activas} no activas más` : ''}>{u.subs_activas}</span>
                          : <span style={{ color: '#cbd5e1' }}>—</span>}
                      </td>
                      <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                        {u.pedidos_pagados > 0
                          ? <span style={{ display: 'inline-block', minWidth: 22, padding: '2px 8px', background: '#dbeafe', color: '#1e40af', borderRadius: 999, fontSize: 12, fontWeight: 700 }}>{u.pedidos_pagados}</span>
                          : <span style={{ color: '#cbd5e1' }}>—</span>}
                      </td>
                      <td style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>
                        {fmtFecha(u.created_at)}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <Link
                          to={`/admin/usuarios/${u.id}`}
                          style={{ background: 'transparent', border: '1px solid #cbd5e1', color: '#475569', padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', textDecoration: 'none', display: 'inline-block', whiteSpace: 'nowrap' }}
                          title="Abrir ficha completa del cliente">
                          📄 Ver ficha
                        </Link>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {modalInvitar && (
          <ModalInvitar
            onClose={() => setModalInvitar(false)}
            onCreated={(r) => {
              setModalInvitar(false)
              setMsg({ tipo: 'ok', txt: `✅ Invitación enviada a ${r.email}. Va a recibir un mail con link para crear su contraseña.` })
              cargar()
              setTimeout(() => setMsg(null), 8000)
            }}
          />
        )}

        {modalInvitarCliente && (
          <ModalInvitar
            rolFijo="cliente"
            onClose={() => setModalInvitarCliente(false)}
            onCreated={(r) => {
              setModalInvitarCliente(false)
              setMsg({ tipo: 'ok', txt: `✅ Invitación enviada a ${r.email}. Cuando cree su cuenta, abrí su ficha para habilitarle transferencia.` })
              cargar()
              setTimeout(() => setMsg(null), 8000)
            }}
          />
        )}

      </div>

      <style>{`
        .usu-table { width: 100%; border-collapse: collapse; font-size: 14px; }
        .usu-table thead th {
          text-align: left; padding: 12px 14px;
          background: #f8fafc; border-bottom: 1px solid #e5e7eb;
          font-size: 12px; text-transform: uppercase; color: #6b7280;
          font-weight: 700; letter-spacing: .03em;
        }
        .usu-table tbody td {
          padding: 12px 14px; border-bottom: 1px solid #f1f5f9; vertical-align: top;
        }
        .usu-table tbody tr:hover { background: #f8fafc; }
        .usu-pill {
          display: inline-block; padding: 3px 10px;
          border-radius: 999px; font-size: 11px; font-weight: 700;
        }
        .ap-filtros { display: flex; gap: 6px; flex-wrap: wrap; }
        .ap-filtro {
          padding: 8px 14px; border: 1px solid #e5e7eb; background: #fff;
          border-radius: 999px; cursor: pointer; font-size: 13px; font-weight: 600;
          color: #475569; display: inline-flex; align-items: center; gap: 6px;
        }
        .ap-filtro em { font-style: normal; opacity: .7; font-size: 11px; }
        .ap-filtro.is-active { background: #1a3461; color: #fff; border-color: #1a3461; }
        .ap-filtro:hover:not(.is-active) { background: #f1f5f9; }
        .ap-loading { padding: 40px; text-align: center; color: #6b7280; }
        .ap-empty { padding: 60px 20px; text-align: center; color: #6b7280; }
        .ap-empty h3 { color: #1a3461; margin: 16px 0 8px; }

        .usu-btn-invitar {
          background: linear-gradient(135deg, #1a3461, #00a8b5);
          color: #fff; border: 0; padding: 10px 18px;
          border-radius: 10px; font-weight: 700; font-size: 13px;
          cursor: pointer; font-family: inherit;
          box-shadow: 0 2px 8px rgba(26, 52, 97, 0.18);
        }
        .usu-btn-invitar:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(26, 52, 97, 0.25); }

        .usu-msg { padding: 12px 16px; border-radius: 10px; margin: 14px 0; font-weight: 600; font-size: 13px; }
        .usu-msg-ok { background: #DCFCE7; color: #14532D; }
        .usu-msg-error { background: #FEE2E2; color: #991B1B; }

        .usu-modal-overlay {
          position: fixed; inset: 0; background: rgba(15, 27, 54, 0.5);
          display: flex; align-items: center; justify-content: center; z-index: 100;
          backdrop-filter: blur(4px);
        }
        .usu-modal {
          background: #fff; border-radius: 14px; padding: 28px;
          width: min(440px, 90vw); max-height: 90vh; overflow-y: auto;
          box-shadow: 0 20px 60px rgba(15, 27, 54, 0.3);
        }
        .usu-modal h2 { margin: 0 0 6px; font-size: 18px; font-weight: 800; letter-spacing: -0.01em; color: #1a3461; }
        .usu-modal-hint { margin: 0 0 16px; font-size: 13px; color: #64748B; line-height: 1.5; }
        .usu-modal label { display: block; font-size: 12px; font-weight: 600; color: #64748B; margin: 14px 0 5px; }
        .usu-modal input, .usu-modal select {
          width: 100%; padding: 9px 12px; border: 1px solid #e2e8f0; border-radius: 8px;
          font-size: 13px; font-family: inherit; color: #1a3461;
          box-sizing: border-box;
        }
        .usu-modal input:focus, .usu-modal select:focus { outline: none; border-color: #00a8b5; box-shadow: 0 0 0 3px rgba(0, 168, 181, 0.15); }
        .usu-role-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-top: 6px; }
        .usu-role-card {
          background: #fff; border: 2px solid #e2e8f0; border-radius: 10px;
          padding: 10px 12px; cursor: pointer; text-align: left;
          font-family: inherit; transition: all 0.12s;
        }
        .usu-role-card:hover { border-color: #00a8b5; background: #f0fafa; }
        .usu-role-card.is-selected { border-color: #1a3461; background: #f0f4ff; box-shadow: 0 2px 8px rgba(26, 52, 97, 0.1); }
        .usu-role-card strong { display: block; font-size: 13px; color: #1a3461; margin-bottom: 2px; }
        .usu-role-card span { font-size: 11px; color: #64748B; line-height: 1.3; }
        .usu-modal-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 22px; }
        .usu-modal-btn {
          padding: 10px 18px; border-radius: 10px; font-weight: 700; font-size: 13px;
          cursor: pointer; font-family: inherit; border: 0;
        }
        .usu-modal-btn-cancel { background: #fff; border: 1px solid #e2e8f0; color: #64748B; }
        .usu-modal-btn-primary { background: linear-gradient(135deg, #1a3461, #00a8b5); color: #fff; box-shadow: 0 2px 8px rgba(26, 52, 97, 0.18); }
        .usu-modal-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
      `}</style>
    </AdminLayout>
  )
}

function ModalInvitar({ onClose, onCreated, rolFijo = null }) {
  const [email, setEmail] = useState('')
  const [nombre, setNombre] = useState('')
  const [role, setRole] = useState(rolFijo || 'admin')
  const [phone, setPhone] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState(null)

  async function invitar() {
    setError(null)
    if (!email || !email.includes('@')) { setError('Email válido requerido'); return }
    if (!nombre.trim()) { setError('Nombre requerido'); return }
    setEnviando(true)
    try {
      // Pre-check: ¿estos datos ya están en uso? Evita confundir errores DB con "ya existe"
      const { data: dups } = await supabase.rpc('check_profile_duplicate', {
        p_email: email.trim(), p_doc_tipo: null, p_doc: null, p_phone: phone.trim() || null,
      })
      if (dups && dups.length > 0) {
        const porEmail = dups.find(d => d.motivo === 'email')
        const porTel   = dups.find(d => d.motivo === 'telefono')
        if (porEmail) {
          setError(`⚠ El email ${email} ya está en uso por "${porEmail.nombre}" (${porEmail.role}). Abrí su ficha en lugar de crear duplicado.`)
          setEnviando(false); return
        }
        // Teléfono es soft: avisamos pero permitimos
        if (porTel) {
          const ok = confirm(`El teléfono ${phone} ya está usado por "${porTel.nombre}" (${porTel.role}).\n\n¿Crear igual? Pueden ser familia que comparte línea.`)
          if (!ok) { setEnviando(false); return }
        }
      }
      const { data, error } = await supabase.functions.invoke('admin-crear-usuario', {
        body: { email: email.trim(), nombre: nombre.trim(), role, phone: phone.trim() || null },
      })
      if (error) throw new Error(error.message)
      if (data?.error) throw new Error(data.error)
      onCreated({ email, nombre, role })
    } catch (e) {
      setError(e.message)
    } finally {
      setEnviando(false)
    }
  }

  return (
    <div className="usu-modal-overlay" onClick={onClose}>
      <div className="usu-modal" onClick={e => e.stopPropagation()}>
        <h2>{rolFijo === 'cliente' ? '＋ Invitar cliente' : '＋ Invitar usuario'}</h2>
        <p className="usu-modal-hint">
          {rolFijo === 'cliente'
            ? 'Le mandamos un mail con un link para crear su contraseña. Después le habilitás transferencia desde su ficha.'
            : 'Le mandamos un mail con un link para que cree su contraseña.'}
        </p>

        <label>Email</label>
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="gimena@limpack.uy"
          autoFocus
        />

        <label>Nombre</label>
        <input
          value={nombre}
          onChange={e => setNombre(e.target.value)}
          placeholder="Gimena González"
        />

        <label>Teléfono (opcional)</label>
        <input
          value={phone}
          onChange={e => setPhone(e.target.value)}
          placeholder="59899..."
        />

        {!rolFijo && (<>
        <label>Rol</label>
        <div className="usu-role-cards">
          {ROLES_INVITABLES.map(r => (
            <button
              key={r.value}
              type="button"
              className={`usu-role-card ${role === r.value ? 'is-selected' : ''}`}
              onClick={() => setRole(r.value)}
            >
              <strong>{r.label}</strong>
              <span>{r.desc}</span>
            </button>
          ))}
        </div>
        </>)}

        {error && (
          <div className="usu-msg usu-msg-error" style={{ marginTop: 14 }}>
            ❌ {error}
          </div>
        )}

        <div className="usu-modal-actions">
          <button className="usu-modal-btn usu-modal-btn-cancel" onClick={onClose} disabled={enviando}>
            Cancelar
          </button>
          <button
            className="usu-modal-btn usu-modal-btn-primary"
            onClick={invitar}
            disabled={enviando}
          >
            {enviando ? '⏳ Enviando…' : '📧 Enviar invitación'}
          </button>
        </div>
      </div>
    </div>
  )
}
